/********************************************************************
 * ADOBE CONFIDENTIAL
 * __________________
 *
 *  Copyright 2024 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 *******************************************************************/

import { transformAccount } from '../data/transforms';

const ACCOUNT_CONTEXT = 'accountContext';
const CHANNEL_CONTEXT = 'channelContext';

enum EventsList {
  CREATE_ACCOUNT_EVENT = 'create-account',
  SIGN_IN = 'sign-in',
  SIGN_OUT = 'sign-out',
}

const events = {
  // Reference - https://github.com/adobe/commerce-events/blob/main/packages/storefront-events-collector/src/handlers/account/createAccountAEP.ts
  CREATE_ACCOUNT: EventsList.CREATE_ACCOUNT_EVENT,
  // Reference - https://github.com/adobe/commerce-events/blob/main/packages/storefront-events-collector/src/handlers/account/signInAEP.ts
  SIGN_IN: EventsList.SIGN_IN,
  // Reference - https://github.com/adobe/commerce-events/blob/main/packages/storefront-events-collector/src/handlers/account/signOutAEP.ts
  SIGN_OUT: EventsList.SIGN_OUT,
};

export function getAdobeDataLayer() {
  // @ts-ignore
  window.adobeDataLayer = window.adobeDataLayer || [];
  // @ts-ignore
  return window.adobeDataLayer;
}

/**
 * Sets a context in the Adobe Client Data Layer (ACDL)
 * Logic based on: https://github.com/adobe/commerce-events/blob/main/packages/storefront-events-sdk/src/Base.ts#L6
 */
function setContext(name: string, data: any) {
  const adobeDataLayer = getAdobeDataLayer();

  // Clear existing context
  adobeDataLayer.push({
    [name]: null,
  });

  // Set new context
  adobeDataLayer.push({
    [name]: data,
  });
}

/**
 * Sets channel context for AEP events
 */
function setChannelContext() {
  const channelData = {
    _id: 'https://ns.adobe.com/xdm/channels/web',
    _type: 'https://ns.adobe.com/xdm/channel-types/web',
  };

  setContext(CHANNEL_CONTEXT, channelData);
}

/**
 * Pushes an event to the Adobe Client Data Layer (ACDL)
 * Logic based on: https://github.com/adobe/commerce-events/blob/1973d0ce28471ef190fa06dad6359ffa0ab51db6/packages/storefront-events-sdk/src/Base.ts#L34
 */
function pushEvent(event: string, additionalContext?: any) {
  const adobeDataLayer = getAdobeDataLayer();

  adobeDataLayer.push((acdl: any) => {
    const state = acdl.getState ? acdl.getState() : {};

    acdl.push({
      event,
      eventInfo: {
        ...state,
        ...additionalContext,
      },
    });
  });
}

/**
 * References:
 * https://github.com/adobe/commerce-events/blob/main/packages/storefront-events-collector/src/handlers/account/createAccountAEP.ts
 * https://github.com/adobe/commerce-events/blob/main/packages/storefront-events-collector/src/utils/aep/account.ts
 */
function createAccountEvent(eventData: any) {
  const accountData = transformAccount(eventData);

  setContext(ACCOUNT_CONTEXT, accountData);

  pushEvent(events.CREATE_ACCOUNT);
}

/**
 * References:
 * https://github.com/adobe/commerce-events/blob/main/packages/storefront-events-collector/src/handlers/account/signInAEP.ts
 * https://github.com/adobe/commerce-events/blob/main/packages/storefront-events-collector/src/utils/aep/account.ts
 */
function loginEvent(eventData: any) {
  const accountData = transformAccount(eventData);

  setContext(ACCOUNT_CONTEXT, accountData);

  pushEvent(events.SIGN_IN);
}

/**
 * Based on storefront-events-collector - sign out event should not contain accountContext
 * Reference - https://github.com/adobe/commerce-events/blob/main/packages/storefront-events-collector/src/handlers/account/signOutAEP.ts
 */
function logoutEvent() {
  pushEvent(events.SIGN_OUT);
}

const publishEvents = (eventType: string, eventParams: any) => {
  const storeConfigRaw = sessionStorage.getItem('storeConfig');
  const storeConfig = storeConfigRaw ? JSON.parse(storeConfigRaw) : {};

  const eventData = { ...storeConfig, ...eventParams };

  setChannelContext();

  switch (eventType) {
    case EventsList.CREATE_ACCOUNT_EVENT:
      createAccountEvent(eventData);
      break;

    case EventsList.SIGN_IN:
      loginEvent(eventData);
      break;

    case EventsList.SIGN_OUT:
      logoutEvent();
      break;

    default:
      return null;
  }
};

export { EventsList, publishEvents, pushEvent };
